<template>
  <div class="partnet-point-index">
    <p v-if="isLoading">
      <Loading />
    </p>
    <template v-else>

      <Tabs />

      <div class="s-p-3">
        <div class="s-bg-white rounded rounded-2 d-flex partnet-point-index__point">
          <div class="text-center w-50">
            <div class="mb-2">{{ currentPoints.toLocaleString() }}</div>
            <div class="s-text-xs">我的點數</div>
          </div>
          <div class="text-center w-50">
            <div class="s-text-secondary mb-2">{{ expiringPoints.toLocaleString() }}</div>
            <div class="s-text-xs">{{ expiringDate }} 即將到期</div>
          </div>
        </div>
      </div>

      <div class="s-p-3">

        <div>
          <label class="field__label">
            請輸入序號兌換寶雅點數
          </label>
          <div class="d-flex">
            <b-form-input
              v-model="serialNumber"
              size="sm"
              class="field__input flex-fill mr-2"
              placeholder="輸入優惠券代碼"
              :disabled="isExchanging"
            />
            <button
              class="s-btn s-btn-primary s-btn-sm w-auto text-nowrap confirm-btn"
              @click="exchangeCode"
              :disabled="isExchanging || !serialNumber"
            >
              {{ isExchanging ? '處理中...' : '確認' }}
            </button>
          </div>
        </div>

        <div class="partnet-point-index__list" v-if="partnerOrganizations.length > 0">
          <div class="divider">可兌換</div>
          
          <!-- Partner Organizations as main structure -->
          <div v-for="(partnerOrg, orgIdx) in processedPartnerOrganizations" :key="`partner-org-${orgIdx}`">
            <!-- Single Partner Organization Card -->
            <div
              class="s-bg-white rounded rounded-2 d-flex align-items-center list__item mb-3"
              :class="{ 'list__item--sold-out': partnerOrg.all_plans_sold_out }"
              @click="navigateToPartnerOrg(partnerOrg)"
            >
              <div class="list__image px-3" v-if="partnerOrg.image">
                <img :src="partnerOrg.image" alt="" @error="$event.target.style.display='none'">
              </div>
              <div class="flex-fill px-3">
                <div class="list__title mb-2">{{ partnerOrg.name }}</div>

                <!-- Show first few exchange options -->
                <template v-if="partnerOrg.available_plans.length > 0">
                  <div v-for="(exchange, exchIdx) in getDisplayExchanges(partnerOrg)" :key="exchIdx"
                    class="s-text-xs mb-1 list__exchange">
                    <span class="s-text-primary font-weight-bold">{{ exchange.from.value }}</span>&nbsp;{{ exchange.from.unit }}&nbsp;→&nbsp;<span
                      class="s-text-primary font-weight-bold">{{ exchange.to.value }}</span>&nbsp;{{ exchange.to.unit }}
                  </div>

                  <!-- Show "更多方案" if there are multiple plans -->
                  <div v-if="partnerOrg.available_plans.length > 1" class="s-text-xs badge badge-secondary p-1">更多方案</div>
                </template>

                <template v-else-if="partnerOrg.all_plans_sold_out">
                  <div class="s-text-xs text-muted">序號已兌換完畢</div>
                </template>
              </div>
            </div>
          </div>
        </div>

        <div v-if="partnerOrganizations.length === 0 && !isLoading" class="text-center py-4">
          <div class="text-muted">目前暫無可用的兌換方案</div>
        </div>

        <div v-if="errorMessage" class="alert alert-danger mt-3">
          {{ errorMessage }}
        </div>

      </div>

    </template>

    <!-- Success Popup -->
    <SuccessPopup
      :show="showSuccessPopup"
      :message="successMessage"
      @close="showSuccessPopup = false"
    />

    <!-- Error Popup -->
    <ErrorPopup
      :show="showErrorPopup"
      :title="errorPopupMessage"
      @close="handleErrorPopupClose"
    />

  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Loading from '@/components/Page/Liff/Shared/Loading'
import Tabs from '@/components/Page/Web/PartnerMarketing/Tabs.vue'
import SuccessPopup from '@/components/Page/Web/PartnerMarketing/SuccessPopup.vue'
import ErrorPopup from '@/components/Page/Web/PartnerMarketing/ErrorPopup.vue'
import { BFormInput } from 'bootstrap-vue'
import webPartnerMarketing from '@/apis/web/partnerMarketing'

export default {
  components: { Loading, Tabs, SuccessPopup, ErrorPopup, BFormInput },
  data: () => ({
    partnerOrganizations: [],
    isLoading: false,
    errorMessage: '',
    currentPoints: 0,
    expiringPoints: 0,
    expiringDate: '',
    showSuccessPopup: false,
    successMessage: '',
    showErrorPopup: false,
    errorPopupMessage: '',
    serialNumber: '',
    isExchanging: false,
    poyaSsoUrl: null
  }),
  computed: {
    ...mapGetters({
      isAuthenticated: 'web/ec-91-auth/isAuthenticated',
      customer: 'web/ec-91-auth/customer',
      jwtToken: 'web/ec-91-auth/jwtToken',
    }),

    processedPartnerOrganizations() {
      const processed = this.partnerOrganizations.map(partnerOrg => {
        // 過濾掉已兌畢的方案（只在首頁卡片顯示有庫存的方案）
        const availablePlans = partnerOrg.available_plans.filter(plan => !plan.is_sold_out);

        // 判斷活動是否所有方案都已兌畢
        const allPlansSoldOut = partnerOrg.available_plans.length > 0 &&
                                partnerOrg.available_plans.every(plan => plan.is_sold_out);

        return {
          ...partnerOrg,
          available_plans: availablePlans,
          all_plans_sold_out: allPlansSoldOut
        };
      });

      // 排序：售罄的放後面，其餘按 sort 欄位由小到大排序
      return processed.sort((a, b) => {
        // 售罄的放後面
        if (a.all_plans_sold_out && !b.all_plans_sold_out) return 1;
        if (!a.all_plans_sold_out && b.all_plans_sold_out) return -1;

        // 按 sort 欄位排序（由小到大）
        const sortA = a.sort ?? Number.MAX_SAFE_INTEGER;
        const sortB = b.sort ?? Number.MAX_SAFE_INTEGER;
        return sortA - sortB;
      });
    }
  },
  async created() {
    await this.fetchPoyaSsoUrl();
  },
  async mounted() {
    document.title = '合作品牌點數交換';
    this.isLoading = true;
    try {
      await Promise.all([
        this.fetch(),
        this.fetchUserPoints()
      ]);
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    async fetch() {
      this.errorMessage = '';

      try {
        const response = await webPartnerMarketing.getAvailableRewards();

        if (response.data.status === 'success') {
          const data = response.data.data;
          this.partnerOrganizations = data.partner_organizations || [];
        } else {
          this.errorMessage = response.data.message || '載入資料失敗';
        }
      } catch (error) {
        this.errorMessage = '請重新登入';
        console.error('Load data error:', error);
        this.errorPopupMessage = this.errorMessage;
        this.showErrorPopup = true;
      }
    },
    async fetchUserPoints() {
      try {
        const response = await webPartnerMarketing.getUserPoints();
        if (response.data.status === 'success') {
          const data = response.data.data;
          this.currentPoints = data.current_points;
          this.expiringPoints = data.expiring_points || 0;
          this.expiringDate = data.expiring_date || '';
        }
      } catch (error) {
        console.error('Failed to fetch user points:', error);
      }
    },
    async exchangeCode() {
      if (!this.serialNumber || !this.serialNumber.trim()) {
        this.errorPopupMessage = 'POYA 點數兌換';
        this.showErrorPopup = true;
        return;
      }

      this.isExchanging = true;

      try {
        const response = await webPartnerMarketing.exchangeCodeForPoints(this.serialNumber.trim());

        if (response.data.status === 'success') {
          const pointsEarned = response.data.data.points_earned || 0;
          this.successMessage = `POYA 點數 ${pointsEarned.toLocaleString()} 點\n成功兌換！`;
          this.showSuccessPopup = true;

          // 清空輸入框
          this.serialNumber = '';

          // 重新載入點數資料
          await this.fetchUserPoints();
        } else {
          this.errorPopupMessage = response.data.message || '兌換失敗';
          this.showErrorPopup = true;
        }
      } catch (error) {
        console.error('Exchange code error:', error);
        const errorMsg = error.response?.data?.message || '兌換失敗，請稍後再試';
        this.errorPopupMessage = errorMsg;
        this.showErrorPopup = true;
      } finally {
        this.isExchanging = false;
      }
    },
    navigateToPartnerOrg(partnerOrg) {
      if (partnerOrg && partnerOrg.id) {
        this.$router.push({ name: 'WebPartnerMarketingShow', params: { id: partnerOrg.id } });
      }
    },
    getDisplayExchanges(partnerOrg) {
      // Get all exchange options from all plans, limit to first 2
      const allExchanges = [];
      partnerOrg.available_plans.forEach(plan => {
        if (plan.exchange) {
          allExchanges.push(plan.exchange);
        }
      });
      return allExchanges.slice(0, 2); // Show max 2 exchange options
    },
    async fetchPoyaSsoUrl() {
      try {
        const response = await webPartnerMarketing.getSsoUrl();
        this.poyaSsoUrl = response.data.data.url;
      } catch (error) {
        console.warn('Failed to fetch POYA-SSO-URL:', error);
      }
    },
    handleErrorPopupClose() {
      if (this.poyaSsoUrl) {
        window.location.href = this.poyaSsoUrl;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.partnet-point-index {
  margin: -1rem -0.75rem 0;

  &__point {
    font-weight: 500;
    font-size: 32px;
    color: black;
    line-height: 1.5;
    padding: 1rem 0;

    > :first-child {
      border-right: 3px solid #E3E3E3;
    }
  }

  &__list {
    margin-top: 1rem;
    line-height: 150%;


  }
}


.s-text-secondary {
  color: #FE577F !important;
}


.field {
  &:not(:last-of-type) {
    margin-bottom: 24px;
  }

  &__label {
    font-weight: 400;
    font-size: 14px;
    color: var(--liff-primary_text_color);
    margin-bottom: 8px;
    display: block;
  }

  &__desc {
    color: var(--liff-secondary_text_color);
    font-size: 12px;
    line-height: 1;
    margin-top: 4px;
  }

  &__input {
    font-weight: 500;
    font-size: 16px;
    color: var(--liff-primary_text_color);
    border: none;
    border-bottom: 1px solid #e5e5ea;
    background: white;
    padding-bottom: 8px;
    width: 100%;
    border: 1px solid #999999;
    border-radius: 5px;
    padding: 8px 12px;

    &::placeholder {
      color: #b7b7b7;
    }

    &:focus {
      border: 1px solid #E3E3E3;
    }

    &.invalid {
      border-bottom: 1px solid #fe0000;
    }

    &:read-only {
      background-color: #f0f0f0;
    }
  }
}

.list__item {
  margin-bottom: 12px;
  padding: 8px 0;
  box-shadow: 0px 2px 6px 0px #00000033;
  cursor: pointer;

  // 已兌畢反灰效果
  &--sold-out {
    opacity: 0.5;
    filter: grayscale(50%);
  }
}

.list__image {
  border-right: 1px solid #E3E3E3;

  img {
    width: 100px;
    height: 100px;
    object-fit: contain;
  }
}

.list__title {
  font-weight: 500;
  font-size: 16px;
}

.list__exchange {
  color: #333333;
}

.confirm-btn {
  padding: 8px 24px !important;
}

</style>
